import * as React from 'react';

import { useIntl } from 'react-intl';
import { RadialBarChart, RadialBar, ResponsiveContainer, PolarAngleAxis } from 'recharts';

import { RADIAL_BAR_FILLS } from 'src/features/dashboard/utils';
import { dashboardMessages } from 'src/features/dashboard/utils/messages';
import { TOTAL_SPACES_NAME } from 'src/features/statistics/utils';
import {
    SectionBlock,
    SectionBlockBody,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSeparator,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { cn } from 'src/utils';

import type { SpacesStatistic } from 'src/features/statistics/types';

// We can use that as to create a 'fake' statistic zone when an error occurs or no zones are available
const dummyData: SpacesStatistic = {
    parking_id: '',
    parking_name: '',
    total_spaces_count: 0,
    onfield_spaces_free: 0,
    onfield_spaces_busy: 0,
    reserved_spaces: 0,
    calculated_spaces_free: 0,
    calculated_spaces_busy: 0,
};

interface DashboardWidgetZonesProps {
    spacesStatistics?: SpacesStatistic[];
    isLoadingSpacesStatistics: boolean;
    errorUpdateCountSpacesStatistics: number;
}
function DashboardWidgetZones({
    spacesStatistics,
    isLoadingSpacesStatistics,
    errorUpdateCountSpacesStatistics,
}: DashboardWidgetZonesProps) {
    const { formatMessage } = useIntl();
    const [selectedZone, setSelectedZone] = React.useState<SpacesStatistic>();

    const [zones, totalSpaces] = React.useMemo(() => {
        if (!spacesStatistics) return [[dummyData], dummyData];

        // The SmartPark API returns a psuedo-zone with the collective data of all zones. We simply want to filter it here.
        const remainingStatisticSpaces = spacesStatistics.filter((zone) => zone.parking_id !== TOTAL_SPACES_NAME);
        const totalSpaces = spacesStatistics.find((zone) => zone.parking_id === TOTAL_SPACES_NAME) || dummyData;

        return [remainingStatisticSpaces, totalSpaces];
    }, [spacesStatistics]);

    return (
        <SectionBlock className="flex-1">
            <SectionBlockHeader>
                <SectionBlockHead>
                    <SectionBlockTitle>{formatMessage(dashboardMessages.parkingZones)}</SectionBlockTitle>
                </SectionBlockHead>
            </SectionBlockHeader>
            <SectionBlockSeparator />
            <SectionBlockBody className="relative flex h-full w-full items-center justify-center">
                {isLoadingSpacesStatistics && !errorUpdateCountSpacesStatistics && (
                    <Skeleton className="aspect-square size-80 rounded-full" />
                )}
                {!isLoadingSpacesStatistics && <div className="aspect-square w-80"></div>}
                {/* Unfortunately radial bars cannot have different domain from each other, so we need to render
                a new container for each entry. Additionally we have to make some hacky calculations and effects as to get
                the desired effect, since there are no props/implementations for it in the library */}
                {(!isLoadingSpacesStatistics || errorUpdateCountSpacesStatistics > 0) &&
                    totalSpaces &&
                    zones?.map((zone, i) => {
                        const width = 50 + (zones.length < 4 ? (zones.length < 2 ? 2 : 1) : 0) * (60 / 4);
                        const range = (110 - width) / zones.length;
                        const start = width + range * i - 0.5;
                        const end = width + range * (i + 1) + 2.5;
                        return (
                            <ResponsiveContainer
                                key={`${zone.parking_name}-${i}`}
                                className="absolute aspect-square"
                                width="100%"
                                height="100%"
                            >
                                <RadialBarChart
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={`${start}%`}
                                    outerRadius={`${end}%`}
                                    margin={{ top: 15, right: 17.5, bottom: 15, left: 17.5 }}
                                    data={[
                                        {
                                            name: zone.parking_name,
                                            total: zone.total_spaces_count,
                                            occupied: zone.onfield_spaces_busy,
                                            fill: RADIAL_BAR_FILLS[i % RADIAL_BAR_FILLS.length],
                                        },
                                    ]}
                                    startAngle={89}
                                    endAngle={-269}
                                    className={cn(
                                        'pointer-events-none relative transition-opacity',
                                        selectedZone?.parking_id === zone.parking_id ? 'opacity-50' : '',
                                    )}
                                    style={{ zIndex: 6 - i }}
                                >
                                    <RadialBar
                                        background={{ fill: '#f3f4f6', stroke: '#fff', strokeWidth: '1%' }}
                                        cornerRadius="5%"
                                        dataKey="occupied"
                                        stroke="#fff"
                                        strokeWidth="1%"
                                        className="pointer-events-auto"
                                        onMouseOver={() => setSelectedZone(zone.parking_id ? zone : undefined)}
                                        onMouseLeave={() => setSelectedZone(undefined)}
                                    />
                                    <PolarAngleAxis
                                        type="number"
                                        style={{ opacity: '0%', visibility: 'hidden' }}
                                        domain={[0, zone.total_spaces_count]}
                                    />
                                </RadialBarChart>
                            </ResponsiveContainer>
                        );
                    })}
                {(!isLoadingSpacesStatistics || errorUpdateCountSpacesStatistics > 0) && totalSpaces && (
                    <div className="absolute inset-0 m-auto flex size-32 flex-col items-center justify-center gap-2">
                        <p className="text-sm text-gray-500">
                            {selectedZone?.parking_name ?? formatMessage(dashboardMessages.totalSpaces)}
                        </p>
                        <p className="text-sm">
                            <span className="text-3xl font-semibold">
                                {selectedZone?.onfield_spaces_free ?? totalSpaces?.onfield_spaces_free}
                            </span>
                            /{selectedZone?.total_spaces_count ?? totalSpaces?.total_spaces_count}
                        </p>
                    </div>
                )}
            </SectionBlockBody>
        </SectionBlock>
    );
}

export default DashboardWidgetZones;
